"use client";

import type { SupWeb3Balance, SupWeb3DepositStep, SupWeb3TxSingleVault } from "@/types";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { trackBagOpen } from "@/utils/analytics";
import { BigNumber, FixedNumber } from "@ethersproject/bignumber";
import { parseUnits } from "viem";
import debounce from "lodash.debounce";

import RouteStateProvider from "@/components/Web3/Common/Route/State";
import Web3DepositSuperPoolFinished from "@/components/Web3/Deposit/SuperPoolFinished";
import { useWeb3DebugContext } from "@/components/Web3/Debug/State";
import Web3CommonHeader from "@/components/Web3/Common/Header";
import Web3CommonVaultAccordion from "@/components/Web3/Common/Vault/Accordion";
import Web3DepositFundingBalance from "@/components/Web3/Deposit/Funding/Balance";
import Web3StepConfirmation from "@/components/Web3/Deposit/Confirmation";
import Web3CommonFinished from "@/components/Web3/Common/Finished";
import Web3DepositFundingAmount from "@/components/Web3/Deposit/Funding/Amount";
import AmountState, { type AmountStateInstance } from "@/components/Web3/Common/AmountState";
import { useSimulationState } from "@/components/Web3/Common/Simulation/State";

import useWeb3UserAddress from "@/hooks/web3/useWeb3UserAddress";
import useDepositCalculate from "@/hooks/tx-flow/useDepositCalculate";
import { useStoredData, useStoredDataDispatch } from "@/store/Context";

import { twMerge } from "tailwind-merge";

const DEBOUNCE_DELAY = 500;

interface Web3DepositProps {
  vaults: SupWeb3TxSingleVault[];
  className?: string;
}

export default function Web3Deposit({ className, vaults }: Web3DepositProps) {
  const storedData = useStoredData();
  const storedDispatch = useStoredDataDispatch();
  const { reset } = useWeb3DebugContext();
  const { runSimulation, simulated, loading } = useSimulationState();
  const userAddress = useWeb3UserAddress();

  useEffect(() => {
    if (!simulated && !loading) {
      runSimulation?.(vaults);
    }
  }, [loading, runSimulation, simulated, vaults]);

  const amountStateRef = useRef<AmountStateInstance>(null);
  const [step, setStep] = useState<SupWeb3DepositStep>("Default" as SupWeb3DepositStep);
  const [vaultList, setVaultList] = useState<SupWeb3TxSingleVault[]>(vaults);
  const [totalGasCost, setTotalGasCost] = useState<number | undefined>(undefined);

  const [selectedBalance, setSelectedBalance] = useState<SupWeb3Balance | undefined>(undefined);
  const [transferTx, setTransferTx] = useState<string | undefined>(undefined);
  const [pauseRequests, setPauseRequests] = useState<boolean>(false);
  const [isErc20, setIsErc20] = useState<boolean>(false);
  const [simulate, setSimulate] = useState<boolean>(false);
  const [apedIn, setApeIn] = useState<boolean>(false);

  const handleSelectedBalanceChange = useCallback(
    (balance: SupWeb3Balance | undefined) => {
      setSelectedBalance(balance);
      reset?.();
      setStep(balance ? "Calculate" : "Default");

      setVaultList(currentVaultList =>
        currentVaultList.map(vault => ({
          ...vault,
          amount: undefined,
          amountInFiat: undefined,
          // @todo: drop this
          isLoading: false,
        }))
      );

      storedDispatch({
        type: "setTxCartValue",
        txCartValue: selectedBalance
          ? {
              cart: "deposit",
              selectedBalance: selectedBalance,
              totalAmount: "",
            }
          : undefined,
      });
      amountStateRef.current?.updateTotalAmount("");
      setApeIn(true);
      setTotalGasCost(undefined);
    },
    [reset, selectedBalance, storedDispatch]
  );

  useEffect(() => {
    if (vaults.length !== vaultList.length) {
      setVaultList(currentVaultList => vaults.map(vault => currentVaultList.find(v => v.id === vault.id) ?? vault));
    }
  }, [vaultList.length, vaults]);

  useEffect(() => {
    if (vaultList.length === 0 && selectedBalance && step !== "Finished") {
      handleSelectedBalanceChange(undefined);
    }
  }, [handleSelectedBalanceChange, selectedBalance, step, vaultList.length]);

  useEffect(() => {
    if (totalGasCost && step !== "Calculate") {
      setTotalGasCost(undefined);
    }
  }, [totalGasCost, step]);

  const handleChange = useRef(
    debounce((vaultAmountMap: Record<string, string | undefined>, vaultList: SupWeb3TxSingleVault[], selectedBalance?: SupWeb3Balance, amount?: string) => {
      const newVaultList = vaultList.map(vault => {
        const incomingValue = isNaN(parseFloat(vaultAmountMap[vault.id] ?? "0")) ? "0" : vaultAmountMap[vault.id] ?? "0";
        const amount = FixedNumber.fromValue(BigNumber.from(parseUnits(incomingValue, selectedBalance?.decimals ?? 18)), selectedBalance?.decimals ?? 18);

        const amountInFiat = amount?.mulUnsafe(
          FixedNumber.fromValue(BigNumber.from(parseUnits(`${selectedBalance?.price ?? "0"}`, selectedBalance?.decimals ?? 18)), selectedBalance?.decimals ?? 18)
        );
        return {
          ...vault,
          amount,
          amountInFiat,
        };
      });

      const totalAmount = amount ? (isNaN(parseFloat(amount)) ? undefined : amount) : undefined;

      storedDispatch({
        type: "setTxCartValue",
        txCartValue: {
          cart: "deposit",
          selectedBalance: selectedBalance,
          totalAmount: totalAmount,
        },
      });

      setTotalGasCost(undefined);
      setVaultList(newVaultList);
    }, DEBOUNCE_DELAY)
  ).current;

  const handleVaultAmountChange = useCallback(
    (vaultAmountMap: Record<string, string | undefined>, amount: string) => {
      handleChange(vaultAmountMap, vaultList, selectedBalance, amount);
    },
    [handleChange, vaultList, selectedBalance]
  );

  useEffect(() => {
    if (vaults.length > 0 && userAddress) {
      trackBagOpen(vaults, "deposit", userAddress);
    }
  }, [userAddress, vaults]);

  // ApeIn processing
  useEffect(() => {
    if (!apedIn) {
      if (storedData.txCartValue && storedData.txCartValue.cart === "deposit") {
        if (!selectedBalance && storedData.txCartValue.selectedBalance) {
          handleSelectedBalanceChange(storedData.txCartValue.selectedBalance as SupWeb3Balance);
        }
        if (storedData.txCartValue.totalAmount) {
          amountStateRef.current?.updateTotalAmount(storedData.txCartValue.totalAmount);
          setSimulate(true);
        }
        if (storedData.txCartValue.step) {
          setStep(storedData.txCartValue.step);
          setApeIn(true);
        }
      }
    }
  }, [apedIn, handleSelectedBalanceChange, selectedBalance, storedData.txCartValue, storedDispatch, vaults]);

  const depositMultiCalculate = useDepositCalculate(selectedBalance, vaultList, pauseRequests);

  return (
    <AmountState ref={amountStateRef} onVaultChange={handleVaultAmountChange}>
      <RouteStateProvider type="deposit" txCalculate={depositMultiCalculate} vaultList={vaultList}>
        <div className={twMerge("flex h-full max-h-screen w-full flex-grow flex-col items-stretch overflow-x-hidden p-6 pt-0 lg:justify-between", className)}>
          <Web3CommonHeader step={step} setStep={setStep} />

          {["Default", "Calculate"].includes(step) && (
            <>
              {selectedBalance && (
                <Web3DepositFundingAmount setVaultList={setVaultList} vaultList={vaultList} selectedBalance={selectedBalance} onSwitchToken={() => setStep("Balance")} />
              )}

              <Web3CommonVaultAccordion
                setPauseRequests={setPauseRequests}
                step={step}
                vaultList={vaultList}
                setVaultList={setVaultList}
                setStep={setStep}
                selectedBalance={selectedBalance}
                pauseRequests={pauseRequests}
                calculatedRoutes={depositMultiCalculate}
              />

              {vaultList.length > 0 && (
                <Web3StepConfirmation
                  simulate={simulate}
                  setSimulate={setSimulate}
                  setIsErc20={setIsErc20}
                  totalGasCost={totalGasCost}
                  setTotalGasCost={setTotalGasCost}
                  vaultList={vaultList}
                  step={step}
                  setStep={setStep}
                  setTransferTx={setTransferTx}
                  selectedBalance={selectedBalance}
                  setPauseRequests={setPauseRequests}
                  calculatedRoutes={depositMultiCalculate}
                />
              )}
            </>
          )}

          {step === "Balance" && <Web3DepositFundingBalance vaultList={vaultList} onBalanceUpdate={handleSelectedBalanceChange} />}

          {step === "Finished" &&
            transferTx &&
            selectedBalance &&
            (isErc20 ? (
              <Web3DepositSuperPoolFinished vaultList={vaultList} transferTx={transferTx} />
            ) : (
              <Web3CommonFinished vaultList={vaultList} chainId={selectedBalance.chainId as number} type={"deposit"} transferTx={transferTx} selectedBalance={selectedBalance} />
            ))}
        </div>
      </RouteStateProvider>
    </AmountState>
  );
}
